import {
  ClientGroupMembershipStatusEnum,
  type ClientComment
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScrollView } from 'react-native';
import { StyleSheet } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

import { useGetComments } from '@/features/api/hooks/grapevine-comment/useGetComments';
import { useGetGroup } from '@/features/api/hooks/grapevine-group/useGetGroup';
import { useGetPostById } from '@/features/api/hooks/grapevine-post/useGetPostById';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { Comments } from '@/features/posts/components/comments/Comments';
import { CommentEditor } from '@/features/posts/components/comments/editor/CommentEditor';
import { GenericPostDetailView } from '@/features/posts/components/detail/GenericPostDetailView';
import { PostDetailHeader } from '@/features/posts/components/detail/PostDetailHeader';
import { MIN_NUMBER_OF_COMMENTS } from '@/features/posts/constants/params';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import type {
  AppNativeStackScreenProps,
  AppRoute
} from '@/navigation/types/navigation';
import { ScrollToEdgeFab } from '@/shared/components/ScrollToEdgeFab';
import { StateView } from '@/shared/components/state-view/StateView';
import { useScrollToEdge } from '@/shared/hooks/useScrollToEdge';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    wrapper: { flex: 1, backgroundColor: theme.colors.surfaceContainerLow },
    content: { paddingBottom: 32 }
  });

type Props = AppNativeStackScreenProps<typeof AppRoute.PostDetail>;

export const PostDetailScreen: FC<Props> = ({ route, navigation }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'screens.post-detail'
  });

  const { userId } = useAppState();
  const { id: postId } = route.params;
  const {
    data: post,
    isLoading,
    isError,
    isRefetching,
    refetch
  } = useGetPostById(postId);

  useEffect(() => {
    // Todo: pre-populate the navbar {color, size} bevor the view is loaded, maybe use a store for this
    if (isLoading) return;
    navigation.setOptions({
      header: () => <PostDetailHeader postId={postId} navigation={navigation} />
    });
  }, [isLoading, navigation, postId]);

  const { data: group } = useGetGroup(post?.groupId);
  const isMember =
    group?.membershipStatus === ClientGroupMembershipStatusEnum.Approved;

  // #region comments

  const {
    data: comments,
    isLoading: isCommentsLoading,
    refetch: refetchComments,
    isRefetching: isRefetchingComments,
    isError: isCommentsError
  } = useGetComments(post?.postId);

  const [comment, setComment] = useState<ClientComment | undefined>();
  const [replyToComment, setReplyToComment] = useState<
    ClientComment | undefined
  >();
  // #endregion comments

  const scrollRef = useRef<ScrollView | null>(null);
  const scrollToBottom = () => scrollRef.current?.scrollToEnd();

  const [shouldScrollToEnd, setShouldScrollToEnd] = useState<boolean>(false);

  const handleScrollToEnd = useCallback(() => {
    // Scroll to end when new comment is created and content size of the scroll view is changed as the refetching of comments is done
    if (!shouldScrollToEnd) return;
    scrollToBottom();
    setShouldScrollToEnd(false);
  }, [shouldScrollToEnd]);

  const isNumberOfCommentsReached = useMemo(
    () => (comments?.length ?? 0) >= MIN_NUMBER_OF_COMMENTS,
    [comments?.length]
  );

  const postCommentsAllowed = post?.commentsAllowed;
  const creatorId = post?.creator?.id;
  const groupId = post?.groupId;

  const commentsAllowed = useMemo(() => {
    if (userId === creatorId) return true;
    else if (!postCommentsAllowed) return false;
    else if (groupId) return isMember;
    else return true;
  }, [isMember, userId, postCommentsAllowed, creatorId, groupId]);

  const fab = useScrollToEdge({
    scrollRef: scrollRef,
    isActive: isNumberOfCommentsReached
  });

  return (
    <>
      <StateView
        isLoading={isLoading}
        isRefetching={isRefetching || isRefetchingComments}
        isError={isError}
        stateLabels={t('state', { returnObjects: true })}
        contentContainerStyle={styles.content}
        onRefetch={() => {
          refetch();
          refetchComments();
        }}
        backgroundColor={theme.colors.surfaceContainerLow}
        ref={scrollRef}
        onScroll={fab.onScroll}
        onScrollBeginDrag={fab.onScrollBeginDrag}
        onScrollEndDrag={fab.onScrollEndDrag}
        scrollEventThrottle={16}
        onContentSizeChange={handleScrollToEnd}
        keyboardShouldPersistTaps="handled"
      >
        <KeyboardAwareScrollView
          style={styles.wrapper}
          keyboardShouldPersistTaps="handled"
        >
          <GenericPostDetailView {...post} />
          <Comments
            commentsAllowed={commentsAllowed}
            comments={comments}
            isLoading={isCommentsLoading}
            isError={isCommentsError}
            onEdit={setComment}
            onReply={setReplyToComment}
          />
        </KeyboardAwareScrollView>
      </StateView>
      <ScrollToEdgeFab
        visible={fab.visible}
        icon={fab.icon}
        onPress={fab.onPressFab}
      />
      {post && (
        <CommentEditor
          postId={post?.postId}
          postType={post.postType}
          comment={comment}
          replyToComment={replyToComment}
          onCancel={() => {
            setComment(undefined);
            setReplyToComment(undefined);
          }}
          onCommentCreate={() => setShouldScrollToEnd(true)}
          commentsAllowed={commentsAllowed}
        />
      )}
    </>
  );
};
